import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import analytics from 'modules/analytics';
import { parseSessionReplayConfig, surfaceForPath } from 'modules/analytics/sessionReplay';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { FF, useSplitFlagWithConfig } from 'modules/feature-flags';

/**
 * Owns deferred Datadog RUM initialization and per-surface Session Replay
 * recording.
 *
 * RUM init is deferred until the SESSION_REPLAY_CONFIG flag resolves (ready or
 * timeout) because `defaultPrivacyLevel` is fixed at init time and is decided per
 * user. On timeout the flag falls back to the default config (masked, no surface
 * recorded), so we fail closed. Init runs at most once.
 *
 * Once initialized, recording is started or stopped as the user moves between the
 * viewer, worklist and reporter surfaces, according to the per-surface sampling
 * decision made at init. Routes outside those three surfaces leave the current
 * recording state untouched.
 */
export function DatadogInitializer() {
  const [{ treatment, config }, flagLoading] = useSplitFlagWithConfig(FF.SESSION_REPLAY_CONFIG);
  const { called, loading: userLoading, data: currentUser } = useCurrentUser();
  const location = useLocation();
  const [initialized, setInitialized] = useState(() => analytics.isDatadogInitialized());

  // Split keys off the signed-in user's email, so on the login and auth-failure
  // routes the client never becomes ready and `flagLoading` would stay true for
  // the whole page session. RUM used to come up unconditionally in the Analytics
  // constructor, and waiting forever here would lose it exactly where an auth
  // failure needs reporting. Once the current-user query settles with no user,
  // stop waiting on the flag and initialize with the default config.
  const signedOut = called && !userLoading && currentUser?.me.email == null;

  useEffect(() => {
    if (initialized) return;
    if (flagLoading && !signedOut) return;
    // Only an `on` cohort gets the dynamic config. A treatment can carry config
    // whatever its value, and `off` carrying nonzero sample rates or
    // `unmask: true` would record a session the flag says to leave alone.
    analytics.initialize(parseSessionReplayConfig(treatment === 'on' ? config : null));
    setInitialized(true);
  }, [flagLoading, signedOut, initialized, treatment, config]);

  useEffect(() => {
    if (!initialized) return;
    const surface = surfaceForPath(location.pathname);
    if (surface == null) return;
    if (analytics.isRecordingEnabledForSurface(surface)) {
      analytics.startSessionReplayRecording();
    } else {
      analytics.stopSessionReplayRecording();
    }
  }, [initialized, location]);

  return null;
}
